import { Outlet, createFileRoute, redirect } from '@tanstack/react-router';
import { useSuspenseQuery } from '@tanstack/react-query';
import { useEffect } from 'react';

import { AppNavbar } from './-components/app-navbar';
import { useShiftLeaveGuard } from './-components/use-shift';
import type { ErrorComponentProps } from '@tanstack/react-router';
import { AppError } from '~/components/app-error';
import { AppLoadingScreen } from '~/components/app-loading-screen';
import { isSignedIn } from '~/lib/auth-client';
import { isAuthError } from '~/lib/app-error';
import { useLiveUpdates } from '~/lib/live';
import { recoverSignedOutSession } from '~/lib/session-recovery';
import { getPostHog } from '~/lib/posthog';
import { useTRPC } from '~/lib/trpc';
import { useBrowserPushSession } from '~/notifications/use-browser-push-session';

export const Route = createFileRoute('/_dashboard')({
  // Awaited, so no loader below this runs until the answer is in: a loader
  // reached signed out would only fail with an auth error.
  beforeLoad: async ({ location }) => {
    if (!(await isSignedIn())) {
      throw redirect({
        to: '/sign-in',
        search: { redirect: location.href },
        replace: true,
      });
    }
  },
  pendingComponent: AppLoadingScreen,
  errorComponent: DashboardError,
  component: AuthenticatedDashboard,
});

function AuthenticatedDashboard() {
  const trpc = useTRPC();
  // A dead session surfaces here as an auth error, which `DashboardError`
  // sends to the recovery.
  const { data: currentUser } = useSuspenseQuery(
    trpc.auth.session.getCurrentUser.queryOptions(),
  );
  useBrowserPushSession();

  /** Joined for the analytics property, which takes a scalar — the same comma
   *  form the role is stored in, so a filter there reads like the database. */
  const roles = currentUser.roles.join(',');

  useEffect(() => {
    getPostHog()?.identify(currentUser.id, { roles });
  }, [roles, currentUser.id]);

  /** Mounted here rather than on a page, so they survive moving between tasks,
   *  mailbox and settings — one guard and one socket for the session. The
   *  socket is also what tells the server this tab is open (presence). */
  useShiftLeaveGuard();
  useLiveUpdates();

  return (
    <div className="min-h-screen bg-muted">
      <AppNavbar />
      <main className="flex h-[calc(100vh-64px)] flex-row">
        <Outlet />
      </main>
    </div>
  );
}

function InvalidSessionRecovery() {
  useEffect(() => {
    recoverSignedOutSession();
  }, []);

  return <AppLoadingScreen />;
}

function DashboardError(props: ErrorComponentProps) {
  if (isAuthError(props.error)) {
    return <InvalidSessionRecovery />;
  }

  return <AppError {...props} />;
}
